// A modal dialog on the browser's own <dialog>: focus moves in and back,
// Escape closes it and the page behind can't be reached meanwhile.
import { type ReactNode, useEffect, useRef } from 'react';
import { t } from '@/l10n/strings';
import { IconButton } from '@/ui/controls';

interface Props {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  wide?: boolean;
  /** Called on submit when the body is a form; the dialog stays open. */
  onSubmit?: () => void;
}

export function Dialog({ open, onClose, title, children, actions, wide, onSubmit }: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // Start in the first field when there is one, not on the close button.
      dialog.querySelector<HTMLElement>('.dialog__body input, .dialog__body textarea, .dialog__body select')?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const body = (
    <div className="dialog__body">
      <div className="dialog__head">
        <h2 className="title-serif">{title}</h2>
        <IconButton icon="x" label={t('commonClose')} size="sm" onClick={onClose} />
      </div>
      {children}
      {actions && <div className="dialog__actions">{actions}</div>}
    </div>
  );

  return (
    <dialog
      ref={ref}
      className={`dialog${wide ? ' dialog--wide' : ''}`}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // A click on the backdrop lands on the dialog itself.
        if (e.target === ref.current) onClose();
      }}
    >
      {open &&
        (onSubmit ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            {body}
          </form>
        ) : (
          body
        ))}
    </dialog>
  );
}

/** Asks before something that can't be undone. */
export function ConfirmDialog({ open, title, message, confirmLabel, danger, onConfirm, onClose }: { open: boolean; title: string; message: ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => void; onClose: () => void }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('commonCancel')}
          </button>
          <button
            type="button"
            className={`btn ${danger ? 'btn--danger-solid' : 'btn--action'}`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="body ink2">{message}</p>
    </Dialog>
  );
}
